import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete,
  InputLabel,
} from "@mui/material";
import { useFormik } from "formik";
import {
  getAllNotesApi,
  updatePatientInfoApi,
} from "../../../api/userApi/userApi";
import useHeaders from "../../../helpers/useHeaders";
import useEncryption from "../../../services/useEncryption";
import useToast from "../../../helpers/useToast";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { toast } from "react-toastify";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import { convertToUSFormat } from "../../../helpers/utils";
import { patientInfoSchema } from "../../../services/formValidator/userValidator";
import { getPatientInfo } from "../../../api/leadApi/leadApi";
import { useSelector } from "react-redux";
import { selectAuth } from "../../../redux/slice/authSlice";
import { createAuditLogs } from "../../../api/providerApi/providerApi";
import PdfTemplate from "./PdfTemplate";
import html2pdf from "html2pdf.js";
import { getAllActiveProviderApi } from "../../../api/adminApi/adminApi";

const ContactInfoForm = ({
  selectedLead,
  getclassName,
  handleStatusUpdate,
  handleCopy,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [update, setUpdate] = useState(false);
  const { role } = useSelector(selectAuth);
  const [locations, setLocations] = useState([]);
  const [notesData, setNotesData] = useState([]);
  const headers = useHeaders();
  const toastOptions = useToast();
  const { encryptData, decryptData } = useEncryption();

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      phone_number: "",
      email_address: "",
      backup_phone_number: "",
      city: "",
      state: "",
      country_of_residence: "",
      preferred_contact_method: "",
      created_at: "",
      tags: [],
      locations: "",
      zipCode: "",
    },
    validationSchema: patientInfoSchema,
    onSubmit: async (values) => {
      try {
        const formatedData = {
          ...values,
          name: `${values.first_name} ${values.last_name}`,
        };

        delete formatedData.first_name;
        delete formatedData.last_name;
        const data = encryptData(formatedData);
        const response = await updatePatientInfoApi(
          selectedLead._id,
          data,
          headers
        );
        setPatientData((prevData) => ({
          ...prevData,
          formsortResponse: encryptData({
            ...decryptData(prevData?.formsortResponse),
            answers: {
              ...decryptData(prevData?.formsortResponse)?.answers,
              name: formatedData.name,
              country_of_residence: formatedData.country_of_residence,
              city: formatedData?.city,
              state: formatedData?.state
            },
          }),
        }));


        let obj = {
          event: `${role} has edited the personal information of ${formatedData.name}.`,
        };
        await createAuditLogs({ data: encryptData(obj) }, headers);
        toast.success(response.data.message, toastOptions);
      } catch (error) {
        toast.error(error.response.data.message, toastOptions);
      }
    },
  });


  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setUpdate((prev) => !prev);
  };

  const handleSaveClick = () => {
    setIsEditing(false);
    formik.handleSubmit();
  };

  const handlePreferredContactChange = (event) => {
    formik.setFieldValue("preferred_contact_method", event.target.value);
  };

  useEffect(() => {
    if (!selectedLead) return;

    const fetchData = async () => {

      try {
        const response = await getPatientInfo(selectedLead._id, headers);

        const decryptedData = decryptData(response.data.data);
        setPatientData(decryptedData);
        const formsortResponse = decryptData(decryptedData?.formsortResponse)
        let answers = formsortResponse?.answers;
        // console.log("answers---", answers)
        const fullName = answers?.name || answers?.Enter_firstname || answers?.Enter_firstname2 || "";
        const nameParts = fullName.split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ")|| answers?.Enter_lastname || "";

        formik.setValues({
          first_name: firstName,
          last_name: lastName,
          phone_number: answers?.phone_number || answers?.nbsp || "",
          email_address: answers?.email_address_2 || answers?.email_address_3 || answers?.email_address || answers?.email_address_4 || "",
          backup_phone_number: answers?.backup_phone_number || "",
          city: answers?.city || "",
          state: answers?.state || "",
          country_of_residence: answers?.country_of_residence_ || answers?.country_of_residence || "",
          preferred_contact_method: answers?.phone_number_preferred_contact_met || answers?.preferred_contact_method || "",
          created_at: decryptedData?.created_at || "",
          // tags: answers?.tags || [], 
          tags: formsortResponse?.tags || [], // from direct formsort response
          locations: decryptedData.providerId || "",
          zipCode: answers?.zipCode || "",
        });

        if (response.data.status === true) {
          const notesResponse = await getAllNotesApi(selectedLead._id, headers);
          const decryptedNotesData = decryptData(notesResponse.data.data);
          setNotesData(decryptedNotesData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [selectedLead, update]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await getAllActiveProviderApi(headers);
        const decryptedLocations = decryptData(response.data.data);
        setLocations(decryptedLocations);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchLocations();
  }, []);

  const pdfTemplateRef = useRef();

  const handleDownloadPDF = async () => {
    try {
      // 1. Fetch latest notes before PDF generation
      const notesResponse = await getAllNotesApi(selectedLead._id, headers);
      const decryptedNotesData = decryptData(notesResponse.data.data);
      setNotesData(decryptedNotesData); // Update the state

      // 2. Wait for DOM to update with new notes
      setTimeout(() => {
        const element = pdfTemplateRef.current;
        const options = {
          filename: "Patient_Profile.pdf",
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
        };
        html2pdf().from(element).set(options).save();
      }, 300); // ⏳ slight delay to ensure re-render (tweak if needed) ///

    } catch (error) {
      console.error("Error fetching latest notes for PDF:", error);
    }
  };


  return (
    <>
      <div className="d-fle align-items-center justify-content-between">
        {" "}
        <button className="btn blackoutlineBtn ms-auto mb-3 d-block" onClick={handleDownloadPDF}>
          Download Profile & BVDQ™{" "}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="15"
            viewBox="0 0 11 15"
            fill="none"
          >
            <path
              d="M9.6325 4.60628L6.18135 7.92961V0.122742H4.57544V7.92961L1.12429 4.60628L0 5.68821L5.37842 10.8674L10.7568 5.68821L9.6325 4.60628Z"
              fill="#0F1D2D"
            />
            <path
              d="M0.963867 12.4915H9.79344V14.0373H0.963867V12.4915Z"
              fill="#0F1D2D"
            />
          </svg>
        </button>
        <div className="nameofpat">
          {decryptData(patientData?.formsortResponse)?.answers?.name ||
            decryptData(patientData?.formsortResponse)?.answers?.Enter_firstname ||
            decryptData(patientData?.formsortResponse)?.answers?.Enter_firstname2 ||
            decryptData(patientData?.formsortResponse)?.answers?.Enter_firstname1
          }
          {" "}
           {decryptData(patientData?.formsortResponse)?.answers?.Enter_lastname ||
            decryptData(patientData?.formsortResponse)?.answers?.Enter_lastname ||
            decryptData(patientData?.formsortResponse)?.answers?.Enter_lastname ||
            decryptData(patientData?.formsortResponse)?.answers?.Enter_lastname
          }
        </div>
        {" "}
        
      </div>
      <div className="locationname">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
        >
          <path
            d="M8.31988 8.67994C7.57188 8.67994 6.95988 8.06794 6.95988 7.31994C6.95988 6.57194 7.57188 5.95994 8.31988 5.95994C9.06788 5.95994 9.67988 6.57194 9.67988 7.31994C9.67988 8.06794 9.06788 8.67994 8.31988 8.67994ZM12.3999 7.45594C12.3999 4.98754 10.5979 3.23994 8.31988 3.23994C6.04188 3.23994 4.23988 4.98754 4.23988 7.45594C4.23988 9.04714 5.56588 11.1551 8.31988 13.6711C11.0739 11.1551 12.3999 9.04714 12.3999 7.45594ZM8.31988 1.87994C11.1759 1.87994 13.7599 4.06954 13.7599 7.45594C13.7599 9.71354 11.9443 12.3859 8.31988 15.4799C4.69548 12.3859 2.87988 9.71354 2.87988 7.45594C2.87988 4.06954 5.46388 1.87994 8.31988 1.87994Z"
            fill="black"
          />
        </svg>
        <span>
          {
            decryptData(patientData?.formsortResponse)?.answers
              ?.country_of_residence_
          }
        </span>
      </div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        {/* <FormControlLabel control={<Checkbox defaultChecked />} label="Hide Empty Feilds" /> */}
        <div></div>
        <FormControl sx={{ m: 1, minWidth: 120 }}>
          <Select
            className={`statelabeld ${getclassName(selectedLead?.status)}`}
            value={selectedLead?.status}
            onChange={(event) => handleStatusUpdate(event)}
            displayEmpty
            inputProps={{ "aria-label": "Without label" }}
            size="small"
            // disabled={role === "staff"}
          >
            <MenuItem value="New Lead">New Lead</MenuItem>
            <MenuItem value="Scheduled For Contact">
              Scheduled For Contact
            </MenuItem>
            <MenuItem value="Contact Attempted">Contact Attempted</MenuItem>
            <MenuItem value="Ready To Schedule">Ready To Schedule</MenuItem>
            <MenuItem value="In Process">In Process</MenuItem>
            <MenuItem value="Scheduled">Scheduled</MenuItem>
            <MenuItem value="UnScheduled">Unscheduled</MenuItem>
            <MenuItem value="Archived">Archived</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="row">
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="first_name"
              name="first_name"
              label="Primary Contact First Name"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.first_name}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.first_name && Boolean(formik.errors.first_name)
              }
              helperText={formik.touched.first_name && formik.errors.first_name}
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="last_name"
              name="last_name"
              label="Primary Contact Last Name"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.last_name}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.last_name && Boolean(formik.errors.last_name)
              }
              helperText={formik.touched.last_name && formik.errors.last_name}
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="phone_number"
              name="phone_number"
              label="Phone"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.phone_number}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.phone_number &&
                Boolean(formik.errors.phone_number)
              }
              helperText={
                formik.touched.phone_number && formik.errors.phone_number
              }
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="email_address"
              name="email_address"
              label="Email"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.email_address}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.email_address &&
                Boolean(formik.errors.email_address)
              }
              helperText={
                formik.touched.email_address && formik.errors.email_address
              }
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="backup_phone_number"
              name="backup_phone_number"
              label="Backup Phone"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.backup_phone_number}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.backup_phone_number &&
                Boolean(formik.errors.backup_phone_number)
              }
              helperText={
                formik.touched.backup_phone_number &&
                formik.errors.backup_phone_number
              }
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12 d-flex flex-wrap">
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.preferred_contact_method === "email"}
                disabled={!isEditing}
                onChange={handlePreferredContactChange}
                name="preferred_contact_method"
                value="email"
              />
            }
            label="Email"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formik.values.preferred_contact_method === "phone call"
                }
                disabled={!isEditing}
                onChange={handlePreferredContactChange}
                name="preferred_contact_method"
                value="phone call"
              />
            }
            label="Phone"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.preferred_contact_method === "sms"}
                disabled={!isEditing}
                onChange={handlePreferredContactChange}
                name="preferred_contact_method"
                value="sms"
              />
            }
            label="SMS"
          />
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="created_at"
              label="Date Time"
              className="w-100"
              variant="outlined"
              size="small"
              value={convertToUSFormat(formik.values.created_at)}
              disabled={true}
              onBlur={formik.handleBlur}
              error={
                formik.touched.created_at && Boolean(formik.errors.created_at)
              }
              helperText={formik.touched.created_at && formik.errors.created_at}
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <Autocomplete
              multiple
              freeSolo
              id="tags"
              options={[]}
              disableCloseOnSelect
              value={formik.values.tags || []}
              onChange={(event, newValue) =>
                formik.setFieldValue("tags", newValue)
              }
              onBlur={formik.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  error={formik.touched.tags && Boolean(formik.errors.tags)}
                  helperText={formik.touched.tags && formik.errors.tags}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              disabled={!isEditing}
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="city"
              name="city"
              label="City"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.city}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
            />
          </div>
        </div>

        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="state"
              name="state"
              label="State"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.state}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.state && Boolean(formik.errors.state)}
              helperText={formik.touched.state && formik.errors.state}
            />
          </div>
        </div>

        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="country_of_residence"
              name="country_of_residence"
              label="Country"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.country_of_residence}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.country_of_residence &&
                Boolean(formik.errors.country_of_residence)
              }
              helperText={
                formik.touched.country_of_residence &&
                formik.errors.country_of_residence
              }
            />
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            {role === "admin" ? (
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="locations-label">Locations</InputLabel>
                <Select
                  labelId="locations-label"
                  id="locations"
                  name="locations"
                  disabled={!isEditing}
                  value={formik.values.locations}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.locations && Boolean(formik.errors.locations)
                  }
                  label="Locations"
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location?.organizationName}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.locations && formik.errors.locations && (
                  <div className="error">{formik.errors.locations}</div>
                )}
              </FormControl>
            ) : (
              <TextField
                id="locations"
                name="locations"
                label="Locations"
                className="w-100"
                variant="outlined"
                size="small"
                value={
                  locations.find(
                    (location) => location.id === formik.values.locations
                  )?.organizationName || ""
                }
                disabled={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.locations && Boolean(formik.errors.locations)
                }
                helperText={formik.touched.locations && formik.errors.locations}
              />
            )}
          </div>
        </div>
        <div className="col-lg-6 col-sm-12">
          <div className="formdesign">
            <TextField
              id="zipCode"
              name="zipCode"
              label="Zip Code"
              className="w-100"
              variant="outlined"
              size="small"
              value={formik.values.zipCode}
              disabled={!isEditing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.zipCode && Boolean(formik.errors.zipCode)
              }
              helperText={formik.touched.zipCode && formik.errors.zipCode}
            />
          </div>
        </div>

        {(role === "doctor" ||
          role === "provider" ||
          role === "location" ||
          role === "admin") && (
            <div className="text-end modalactions">
              {!isEditing ? (
                <button
                  type="button"
                  className="btn btn-blacked"
                  onClick={handleEditClick}
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-blacked outline me-2"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-blacked"
                    onClick={handleSaveClick}
                  >
                    Update
                  </button>
                </>
              )}
            </div>
          )}
        <div className="patientId">
          <p>NVM Patient Id </p>
          <Tooltip title="This is the patient's NVM ID" placement="top" arrow>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.75 5.875C10.75 8.56741 8.56741 10.75 5.875 10.75C3.18261 10.75 1 8.56741 1 5.875C1 3.18261 3.18261 1 5.875 1C8.56741 1 10.75 3.18261 10.75 5.875Z"
                stroke="black"
                stroke-width="0.8"
                strokeLinecap="round"
              />
              <path
                d="M5.3335 5.87498V8.04165C5.3335 8.34081 5.576 8.58331 5.87516 8.58331C6.17433 8.58331 6.41683 8.34081 6.41683 8.04165V5.87498C6.41683 5.57582 6.17433 5.33331 5.87516 5.33331C5.576 5.33331 5.3335 5.57582 5.3335 5.87498Z"
                stroke="black"
                stroke-width="0.8"
                strokeLinecap="round"
              />
              <path
                d="M6.41683 3.70835C6.41683 3.4092 6.17433 3.16669 5.87516 3.16669C5.576 3.16669 5.3335 3.4092 5.3335 3.70835C5.3335 4.00751 5.576 4.25002 5.87516 4.25002C6.17433 4.25002 6.41683 4.00751 6.41683 3.70835Z"
                stroke="black"
                stroke-width="0.8"
                strokeLinecap="round"
              />
            </svg>
          </Tooltip>
          {selectedLead?.patientId}
          <svg
            onClick={handleCopy}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.5 8.99995V9.5C7.5 10.0523 7.0523 10.5 6.5 10.5H2.5C1.94771 10.5 1.5 10.0523 1.5 9.5V5.49995C1.5 4.94769 1.94769 4.49998 2.49996 4.49996L3 4.49994M5.5 1.49994H9.5C10.0523 1.49994 10.5 1.94765 10.5 2.49994V6.49995C10.5 7.0522 10.0523 7.49995 9.5 7.49995H5.5C4.94771 7.49995 4.5 7.0522 4.5 6.49995V2.49994C4.5 1.94765 4.94771 1.49994 5.5 1.49994Z"
              stroke="black"
              stroke-width="0.8"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <hr className="lineControls" />
        <div className="col-lg-12 col-sm-12"></div>
      </div>

      <div style={{ display: "none" }}>
        <div ref={pdfTemplateRef}>
          <PdfTemplate patientData={patientData} notesData={notesData} />
        </div>
      </div>
    </>
  );
};

export default ContactInfoForm;
